import { Routes } from '@angular/router';
import { AuthGuard, AuthLogoutGuard, RouterTools, SolErrorPageComponent } from '@sol/components';
import {
  generatedCatalogDocumentDefaultRoute,
  generatedCatalogDocuments,
} from './generated/catalog-documents.generated';
import { SolCatalogRouteId } from './models/sol-catalog-route-id';

export const routes: Routes = [
  // Apps
  RouterTools.ofExternal(SolCatalogRouteId.app_platform, '{{ASM_PLATFORM_NG_URL}}', []),
  RouterTools.ofExternal(SolCatalogRouteId.app_partner, '{{ASM_PLATFORM_NG_URL}}/partner', []),
  RouterTools.ofExternal(SolCatalogRouteId.app_admin, '{{ASM_PLATFORM_NG_URL}}/admin', []),

  // Documents
  RouterTools.ofRedirect(SolCatalogRouteId.app_catalog, SolCatalogRouteId.documents_sol_catalog),
  RouterTools.ofRedirect(SolCatalogRouteId.documents, generatedCatalogDocumentDefaultRoute),
  RouterTools.ofRedirect(SolCatalogRouteId.documents_ux_foundation, generatedCatalogDocumentDefaultRoute),
  RouterTools.ofLazy(SolCatalogRouteId.documents_sol_catalog, () =>
    import('./components/documents/sol-catalog-page/sol-catalog-page.component').then(m => m.SolCatalogPageComponent),
  ),
  ...generatedCatalogDocuments.map(document =>
    RouterTools.ofLazy(document.routePath, () =>
      import('./components/documents/sol-catalog-document-page/sol-catalog-document-page.component').then(
        m => m.SolCatalogDocumentPageComponent,
      ),
    ),
  ),

  // Components: chart
  RouterTools.ofRedirect(SolCatalogRouteId.components, SolCatalogRouteId.components_overview),
  RouterTools.ofLazy(SolCatalogRouteId.components_overview, () =>
    import('./components/components/sol-angular-page/sol-angular-page.component').then(m => m.SolAngularPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_chart, () =>
    import('./components/components/sol-chart-page/sol-chart-page.component').then(m => m.SolChartPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_services, () =>
    import('./components/components/sol-services-page/sol-services-page.component').then(
      m => m.SolServicesPageComponent,
    ),
  ),

  // Components: core
  RouterTools.ofRedirect(SolCatalogRouteId.components_core, SolCatalogRouteId.components_core_button),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_accordion, () =>
    import('./components/components/sol-accordion-page/sol-accordion-page.component').then(
      m => m.SolAccordionPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_alert, () =>
    import('./components/components/sol-alert-page/sol-alert-page.component').then(m => m.SolAlertPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_avatar, () =>
    import('./components/components/sol-avatar-page/sol-avatar-page.component').then(m => m.SolAvatarPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_badge, () =>
    import('./components/components/sol-badge-page/sol-badge-page.component').then(m => m.SolBadgePageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_banner, () =>
    import('./components/components/sol-banner-page/sol-banner-page.component').then(m => m.SolBannerPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_budget, () =>
    import('./components/components/sol-budget-page/sol-budget-page.component').then(m => m.SolBudgetPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_button, () =>
    import('./components/components/sol-button-page/sol-button-page.component').then(m => m.SolButtonPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_button_group, () =>
    import('./components/components/sol-button-group-page/sol-button-group-page.component').then(
      m => m.SolButtonGroupPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_card, () =>
    import('./components/components/sol-card-page/sol-card-page.component').then(m => m.SolCardPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_checkbox, () =>
    import('./components/components/sol-checkbox-page/sol-checkbox-page.component').then(
      m => m.SolCheckboxPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_spinner, () =>
    import('./components/components/sol-spinner-page/sol-spinner-page.component').then(m => m.SolSpinnerPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_toggle_chip, () =>
    import('./components/components/sol-toggle-chip-page/sol-toggle-chip-page.component').then(
      m => m.SolToggleChipPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_comment, () =>
    import('./components/components/sol-comment-page/sol-comment-page.component').then(m => m.SolCommentPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_date_field, () =>
    import('./components/components/sol-date-field-page/sol-date-field-page.component').then(
      m => m.SolDateFieldPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_diff, () =>
    import('./components/components/sol-diff-page/sol-diff-page.component').then(m => m.SolDiffPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_empty_state, () =>
    import('./components/components/sol-empty-state-page/sol-empty-state-page.component').then(
      m => m.SolEmptyStatePageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_icon, () =>
    import('./components/components/sol-icon-page/sol-icon-page.component').then(m => m.SolIconPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_image, () =>
    import('./components/components/sol-image-page/sol-image-page.component').then(m => m.SolImagePageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_layout, () =>
    import('./components/components/sol-layout-page/sol-layout-page.component').then(m => m.SolLayoutPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_list, () =>
    import('./components/components/sol-list-page/sol-list-page.component').then(m => m.SolListPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_loader, () =>
    import('./components/components/sol-loader-page/sol-loader-page.component').then(m => m.SolLoaderPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_file_upload, () =>
    import('./components/components/sol-file-upload-page/sol-file-upload-page.component').then(
      m => m.SolFileUploadPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_log, () =>
    import('./components/components/sol-log-page/sol-log-page.component').then(m => m.SolLogPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_markdown, () =>
    import('./components/components/sol-markdown-page/sol-markdown-page.component').then(
      m => m.SolMarkdownPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_big_checkbox, () =>
    import('./components/components/sol-big-checkbox-page/sol-big-checkbox-page.component').then(
      m => m.SolBigCheckboxPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_big_radiobutton, () =>
    import('./components/components/sol-big-radiobutton-page/sol-big-radiobutton-page.component').then(
      m => m.SolBigRadiobuttonPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_filter_field, () =>
    import('./components/components/sol-filter-field-page/sol-filter-field-page.component').then(
      m => m.SolFilterFieldPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_query_field, () =>
    import('./components/components/sol-query-field-page/sol-query-field-page.component').then(
      m => m.SolQueryFieldPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_radio_button, () =>
    import('./components/components/sol-radio-button-page/sol-radio-button-page.component').then(
      m => m.SolRadioButtonPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_relative_date_field, () =>
    import('./components/components/sol-relative-date-field-page/sol-relative-date-field-page.component').then(
      m => m.SolRelativeDateFieldPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_scroll_view, () =>
    import('./components/components/sol-scroll-view-page/sol-scroll-view-page.component').then(
      m => m.SolScrollViewPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_select, () =>
    import('./components/components/sol-select-page/sol-select-page.component').then(m => m.SolSelectPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_view_box, () =>
    import('./components/components/sol-view-box-page/sol-view-box-page.component').then(
      m => m.SolViewBoxPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_severity, () =>
    import('./components/components/sol-severity-page/sol-severity-page.component').then(
      m => m.SolSeverityPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_snippet, () =>
    import('./components/components/sol-snippet-page/sol-snippet-page.component').then(m => m.SolSnippetPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_steps, () =>
    import('./components/components/sol-steps-page/sol-steps-page.component').then(m => m.SolStepsPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_switch, () =>
    import('./components/components/sol-switch-page/sol-switch-page.component').then(m => m.SolSwitchPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_table, () =>
    import('./components/components/sol-table-page/sol-table-page.component').then(m => m.SolTablePageComponent),
  ),
  RouterTools.ofRedirect(SolCatalogRouteId.components_tools, SolCatalogRouteId.components_tools_workbench),
  RouterTools.ofLazy(SolCatalogRouteId.components_tools_workbench, () =>
    import('./components/components/sol-workbench-page/sol-workbench-page.component').then(
      m => m.SolWorkbenchPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_tools_variant, () =>
    import('./components/components/sol-variant-page/sol-variant-page.component').then(m => m.SolVariantPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_tag, () =>
    import('./components/components/sol-tag-page/sol-tag-page.component').then(m => m.SolTagPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_tag_color, () =>
    import('./components/components/sol-tag-color-page/sol-tag-color-page.component').then(
      m => m.SolTagColorPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_text_box, () =>
    import('./components/components/sol-text-box-page/sol-text-box-page.component').then(
      m => m.SolTextBoxPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_text_area, () =>
    import('./components/components/sol-text-area-page/sol-text-area-page.component').then(
      m => m.SolTextAreaPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_text_field, () =>
    import('./components/components/sol-text-field-page/sol-text-field-page.component').then(
      m => m.SolTextFieldPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_time_field, () =>
    import('./components/components/sol-time-field-page/sol-time-field-page.component').then(
      m => m.SolTimeFieldPageComponent,
    ),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_tooltip, () =>
    import('./components/components/sol-tooltip-page/sol-tooltip-page.component').then(m => m.SolTooltipPageComponent),
  ),
  RouterTools.ofLazy(SolCatalogRouteId.components_core_trend, () =>
    import('./components/components/sol-trend-page/sol-trend-page.component').then(m => m.SolTrendPageComponent),
  ),

  // Account
  RouterTools.ofLazy(SolCatalogRouteId.account_preferences, () =>
    import('./components/account/sol-preferences-page/sol-preferences-page.component').then(
      m => m.SolPreferencesPageComponent,
    ),
  ),
  {
    path: 'logout',
    canActivate: [AuthGuard, AuthLogoutGuard],
    component: SolErrorPageComponent,
    data: {
      id: SolCatalogRouteId.account_logout,
    },
  },
];
